import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// VisitorOrb — what a PERSON looks like to everyone else in the city.
//
// Khari, 2026-10-23: *"We have objects for users without bodies. it is currently
// a pill object with a faceplate. users will want to interact with each other,
// so we will need to keep some sort of visible icon for them. We can use an orb
// for now, or something simple until we determine what we want them to use."*
//
// Bodies belong to the artists now — they live in the city in them, and nobody
// walks as one (2026-10-23). A visitor is a light hanging at about head height:
// easy to spot across a street, easy to walk up to, and impossible to mistake
// for an artist. Their own view stays first person; this is only what OTHER
// people see. One component, so the street and the listening room's chairs can
// never draw two different ideas of "a person".
//
// ⚠ PLACEHOLDER BY DESIGN. When visitors get a real look, this is the one file
// that changes.
//
// @param member  Signed-in visitors glow warm (the district's amber); guests a
//                cool white. The only distinction it makes.
// @param height  Centre of the orb above this group's origin (the floor, or a
//                chair's cushion). ⚠ THIS WORLD IS NOT IN METRES: the street eye
//                is 0.75 and the residents stand about that tall, so a head-height
//                orb sits near 0.7, not 1.7.
// @param seed    Anything stable per person; keeps a crowd of orbs from bobbing
//                in lockstep.
const MEMBER = '#ffb25e';
const GUEST = '#d6defa';

function phaseOf(seed) {
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return ((h >>> 0) % 1000) / 1000 * Math.PI * 2;
}

export default function VisitorOrb({ member = false, height = 0.7, seed = '' }) {
  const ref = useRef();
  const phase = useRef(phaseOf(seed));
  const color = member ? MEMBER : GUEST;
  // A slow float, not a bounce — it should read as alive, not as busy.
  useFrame(({ clock }) => {
    if (ref.current) ref.current.position.y = height + Math.sin(clock.elapsedTime * 1.4 + phase.current) * 0.025;
  });
  return (
    <group ref={ref} position={[0, height, 0]}>
      <mesh>
        <sphereGeometry args={[0.085, 32, 20]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={1.1} roughness={0.3} metalness={0.05} />
      </mesh>
      {/* The halo: additive, no depth write, so it glows over the street rather
          than cutting a hole in it. */}
      <mesh>
        <sphereGeometry args={[0.15, 24, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.18} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>
    </group>
  );
}
